import { unreachable } from 'module/helpers/unreachable';
import { useTranslation } from 'module/hooks/use-translation';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { PageLoading } from 'module/ui/components/atoms/loading';
import { ActionBar } from 'module/ui/components/molecules/action-bar';
import { FilterBar } from 'module/ui/components/molecules/filter-bar';
import {
  HeaderWithIconTitle,
  Page,
} from 'module/ui/components/molecules/page/page';
import { Argon as ArgonTemplate } from 'module/ui/components/templates/argon';
import {
  Content,
  ContentWrapper,
  Layout,
  StyledPage,
} from 'module/ui/components/templates/argon/components';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { faSignal } from '@fortawesome/pro-solid-svg-icons';
import { useNavigate } from '@rocketlane/rocket-router';
import {
  Button,
  EmptyState as RocketlaneEmptyState,
  Icon,
} from '@rocketlane/ui';
import {
  GetSignalConfigurationFromPayload,
  SignalConfiguration,
  SignalType,
} from 'api/signal/types';
import {
  useDeleteSignalConfigurationMutation,
  useGetAllSignalConfigurationsQuery,
  useUpdateSignalConfigurationMutation,
} from 'queries/signal';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { SignalList } from 'components/organisms/signal/list';
import { RecommendedSignalsEmptyState } from 'components/organisms/signal/recommended-signals-empty-state';
import { SignalPreConfigurationModal } from 'components/organisms/signal/signal-pre-configure-modal';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const Argon = styled(ArgonTemplate)`
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;

  ${StyledPage} {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    min-height: 0;
  }

  ${Page.Content} {
    overflow: hidden;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }

  ${Layout} {
    overflow: hidden;
    flex: 1;
    min-height: 0;
  }

  ${Content} {
    border: none;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  ${FilterBar} {
    flex-shrink: 0;
  }

  ${ContentWrapper} {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  ${ActionBar} {
    box-shadow: none;
  }
`;

const ScrollableListContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding-right: 8px;
  scrollbar-gutter: stable;
`;

const SignalListEmptyStateRoot = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 320px;
  padding: var(--spacing-09) var(--spacing-08);
`;

export interface SignalPermissions {
  canWrite: boolean;
  canDelete: boolean;
}

interface SignalConfigurationListPageProps {
  signalPermissions?: SignalPermissions;
  isModuleFed?: boolean;
  onNavigateToSignal?: (signalConfigurationId: number) => void;
}
export const SignalConfigurationListPage = ({
  signalPermissions,
  isModuleFed,
  onNavigateToSignal,
}: SignalConfigurationListPageProps) => {
  const title = useTranslation('signals');
  const signalTypeRiskLabel = useTranslation(
    'signal_configuration.signal_type.risk.name',
  );
  const signalTypeOpportunityLabel = useTranslation(
    'signal_configuration.signal_type.opportunity.name',
  );
  const signalTypeOperationalLabel = useTranslation(
    'signal_configuration.signal_type.operational.name',
  );

  const { data: signals, isLoading: isSignalsLoading } =
    useGetAllSignalConfigurationsQuery();

  // Testing helper: append `?forceSignalsEmpty=1` to the URL to preview empty state UI.
  // const forceSignalsEmpty =
  //   typeof window !== 'undefined' &&
  //   new URLSearchParams(window.location.search).get('forceSignalsEmpty') === '1';
  // const effectiveSignals = forceSignalsEmpty ? [] : (signals ?? []);
  const effectiveSignals = signals ?? [];

  const { mutateAsync: deleteSignalConfigurationApi } =
    useDeleteSignalConfigurationMutation();

  const { mutateAsync: updateSignalConfigurationApi } =
    useUpdateSignalConfigurationMutation();

  const [signalNameInputModalVisible, setSignalNameInputModalVisible] =
    useState(false);
  const [showRecommendedSignals, setShowRecommendedSignals] = useState(true);

  const openSignalNameInputModal = useCallback(() => {
    setSignalNameInputModalVisible(true);
  }, []);

  const closeSignalNameInputModal = useCallback(() => {
    setSignalNameInputModalVisible(false);
  }, []);

  const navigate = useNavigate();

  const navigateToSignalConfiguration = useCallback(
    (signalConfiguration: SignalConfiguration) => {
      if (onNavigateToSignal) {
        onNavigateToSignal(signalConfiguration.signalConfigurationId);
      } else {
        navigate({
          pathname: `/signal-agents/${signalConfiguration.signalConfigurationId}`,
        });
      }
    },
    [navigate, onNavigateToSignal],
  );

  const mapSignalTypeToTitleCase = useCallback(
    (signalType: SignalType): string => {
      switch (signalType) {
        case SignalType.Risk:
          return signalTypeRiskLabel;
        case SignalType.Opportunity:
          return signalTypeOpportunityLabel;
        case SignalType.Operational:
          return signalTypeOperationalLabel;
        default:
          return unreachable(signalType);
      }
    },
    [
      signalTypeRiskLabel,
      signalTypeOpportunityLabel,
      signalTypeOperationalLabel,
    ],
  );

  const onNewSignalName = useCallback(
    (payload: GetSignalConfigurationFromPayload): Promise<void> => {
      closeSignalNameInputModal();

      const signalTypeTitleCase = mapSignalTypeToTitleCase(payload.signalType);

      navigate('/signal-agents/create', {
        state: { signalType: signalTypeTitleCase },
      });

      return Promise.resolve();
    },
    [closeSignalNameInputModal, mapSignalTypeToTitleCase, navigate],
  );

  const onSetupRecommendedSignal = useCallback(
    (signalType: SignalType) => {
      navigate('/signal-agents/create', {
        state: { signalType: mapSignalTypeToTitleCase(signalType) },
      });
    },
    [navigate, mapSignalTypeToTitleCase],
  );

  const onDeleteSignalConfiguration = useCallback(
    (signalConfiguration: SignalConfiguration) => {
      deleteSignalConfigurationApi(signalConfiguration.signalConfigurationId)
        .then(() => {})
        .catch(() => {});
    },
    [deleteSignalConfigurationApi],
  );

  const onStatusChange = useCallback(
    (
      signalConfiguration: SignalConfiguration,
      nextVisibleOnTimeline: boolean,
    ) => {
      updateSignalConfigurationApi({
        ...signalConfiguration,
        visibleOnTimeline: nextVisibleOnTimeline,
      })
        .then(() => {})
        .catch(() => {});
    },
    [updateSignalConfigurationApi],
  );

  const onEnabledChange = useCallback(
    (signalConfiguration: SignalConfiguration, nextIsEnabled: boolean) => {
      updateSignalConfigurationApi({
        ...signalConfiguration,
        isEnabled: nextIsEnabled,
      })
        .then(() => {})
        .catch(() => {});
    },
    [updateSignalConfigurationApi],
  );

  return (
    <PageContainer>
      <Argon
        documentTitle={title}
        title={
          <HeaderWithIconTitle
            icon={<FontAwesomeIcon icon={faSignal} size={20} />}
            title={title}
          />
        }
        description={
          <FormattedMessage id="settings.signal_configuration.page.description" />
        }
        actions={
          isSignalsLoading
            ? {}
            : {
                secondary: (
                  <Button
                    disabled={
                      isModuleFed &&
                      !!signalPermissions &&
                      signalPermissions.canWrite === false
                    }
                    leftIcon={<FontAwesomeIcon icon={faPlus} />}
                    variant="primary"
                    onClick={openSignalNameInputModal}
                  >
                    <FormattedMessage id="settings.signal_configuration.action.create_signal" />
                  </Button>
                ),
              }
        }
        content={
          isSignalsLoading ? (
            <PageLoading />
          ) : (
            <>
              <ScrollableListContainer>
                {effectiveSignals.length === 0 ? (
                  showRecommendedSignals ? (
                    <RecommendedSignalsEmptyState
                      onDismiss={() => setShowRecommendedSignals(false)}
                      onSetup={onSetupRecommendedSignal}
                    />
                  ) : (
                    <SignalListEmptyStateRoot>
                      <RocketlaneEmptyState
                        icon={{ icon: faSignal }}
                        message={
                          <FormattedMessage id="settings.signal_configuration.empty_list.title" />
                        }
                        description={
                          <FormattedMessage id="settings.signal_configuration.page.description" />
                        }
                        primaryAction={{
                          leftIcon: <Icon icon={faPlus} size=".75x" />,
                          onClick: openSignalNameInputModal,
                          disabled:
                            isModuleFed &&
                            !!signalPermissions &&
                            signalPermissions.canWrite === false,
                          children: (
                            <FormattedMessage id="settings.signal_configuration.action.create_signal" />
                          ),
                        }}
                      />
                    </SignalListEmptyStateRoot>
                  )
                ) : (
                  <SignalList
                    isModuleFed={isModuleFed}
                    signalPermissions={signalPermissions}
                    signals={effectiveSignals}
                    onEdit={navigateToSignalConfiguration}
                    onDelete={onDeleteSignalConfiguration}
                    onStatusChange={onStatusChange}
                    onEnabledChange={onEnabledChange}
                  />
                )}
              </ScrollableListContainer>

              {signalNameInputModalVisible ? (
                <SignalPreConfigurationModal
                  onClose={closeSignalNameInputModal}
                  onSubmit={onNewSignalName}
                />
              ) : null}
            </>
          )
        }
      />
    </PageContainer>
  );
};
